import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, Select, SelectSearchDicomTag, Slider } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  SettingBox,
  SettingWrapper,
  StyledReportWrap,
  SwButtonSection,
  InputBox,
  FlexBox,
  RadioLabel,
  ThresholdDisplay,
  Description,
  SliderWrapper,
  ElementBox,
  ElementTitle,
  ElementMetatag,
  ElementRow,
  ElementValue,
  SettingBoxList,
  SettingBoxListTitle,
  SettingBoxListValue,
} from './style';
import { submitDebounce } from 'utils/lib';
import _, { isEmpty } from 'lodash';
import { DicomInfo } from '@pages/SW/Metatag';
import { useCatchLeave } from 'utils/hooks';

interface optionDataType {
  maximumTimeout: number;
  isPacsIncomingT1: boolean;
  nmSliceMax: number;
  nmSliceMin: number;
  t1SliceMax: number;
  t1SliceMin: number;
  threshold: number;
  timeout: number;
}

const tagTitle = ['Sandwich NM', 'T1'];

const NMOption = () => {
  const [dicomTagList, setDicomTagList] = useState<DicomInfo[]>([]);
  const [metaList, setMetaList] = useState<DicomInfo[]>([]);
  const [originMetaList, setOriginMetaList] = useState<DicomInfo[]>([]);
  const [isChanged, setIsChanged] = useState(false);

  const setDialogContents = useSetRecoilState(dialogContentsState);

  const { submitConfirmDialog } = useStaticDialog();
  const { errorHandler, metaListChangeData, getDicomTagAll } = SettingCommonMethods();

  const [data, setData] = useState<optionDataType>({
    maximumTimeout: 0,
    isPacsIncomingT1: false,
    nmSliceMax: 0,
    nmSliceMin: 0,
    t1SliceMax: 0,
    t1SliceMin: 0,
    threshold: 0,
    timeout: 0,
  });

  const [backupData, setBackupData] = useState<optionDataType>({
    maximumTimeout: 0,
    isPacsIncomingT1: false,
    nmSliceMax: 0,
    nmSliceMin: 0,
    t1SliceMax: 0,
    t1SliceMin: 0,
    threshold: 0,
    timeout: 0,
  });

  const isEditCondition = useMemo(() => {
    // slice 값 유무 확인
    const hasTimeout = !!data.timeout.toString() && !!data.maximumTimeout.toString();
    const hasNMSliceMinMax = !!data.nmSliceMin.toString() && !!data.nmSliceMax.toString();
    const hasSliceMinMax = data.isPacsIncomingT1
      ? data.isPacsIncomingT1 && !!data.t1SliceMin.toString() && !!data.t1SliceMax.toString() && hasNMSliceMinMax
      : hasNMSliceMinMax;

    // protocol 유무 확인
    const hasProtocolTag = metaList.filter((list) => !list.value || !list.tag).length === 0;

    return (_.isEqual(data, backupData) && !isChanged) || !hasTimeout || !hasSliceMinMax || !hasProtocolTag;
  }, [data, backupData, isChanged, metaList]);

  useCatchLeave(!isEditCondition);

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */
  const setDataValue = (key: keyof typeof data, value: any) => {
    setData((prevState) => {
      const copy = { ...prevState } as any;
      copy[key] = value;
      if (key === 'isPacsIncomingT1' && !value) {
        copy.t1SliceMin = backupData.t1SliceMin;
        copy.t1SliceMax = backupData.t1SliceMax;
      }

      return copy;
    });
  };

  const changeOnlyNumber = (ev: React.ChangeEvent<HTMLInputElement>, key: keyof typeof data) => {
    let value = ev.target.value;

    if (!value) setDataValue(key, value);
    if (!/^[0-9]+$/g.test(value)) return;
    if (value.length > 1 && value[0] === '0') value = value.slice(1, value.length);

    setDataValue(key, Number(value));
  };

  const blurHandler = (ev: React.FocusEvent<HTMLInputElement>, key: keyof typeof data) => {
    let value: string | number = ev.target.value;

    // 기본값 적용
    if (!value) {
      if (key === 'timeout') value = 30;
      if (key === 'maximumTimeout') value = 3000;
      if (key === 'nmSliceMin') value = 32;
      if (key === 'nmSliceMax') value = 128;
      if (key === 't1SliceMin') value = 60;
      if (key === 't1SliceMax') value = 1024;

      setDataValue(key, value);

      return;
    }

    value = Number(value);

    if (value > 10000) value = 10000;

    //time out 일 경우 5 미만 이면 5로 적용 필요
    // 3600 이상이면 3600
    if (key === 'timeout' || key === 'maximumTimeout') {
      if (value < 5) value = 5;
      if (value > 3600) value = 3600;
    } else if (key === 't1SliceMax' && data.t1SliceMin > value) value = data.t1SliceMin;
    else if (key === 't1SliceMin' && data.t1SliceMax < value) value = data.t1SliceMax;
    else if (key === 'nmSliceMax' && data.nmSliceMin > value) value = data.nmSliceMin;
    else if (key === 'nmSliceMin' && data.nmSliceMax < value) value = data.nmSliceMax;

    setDataValue(key, value);
  };

  /**
   * metaList 변경 또는 유효성 확인
   * @param e
   * @param index
   * @param type
   */
  const metaListChange = useCallback(
    (e: any, index: number, type: string) => {
      metaListChangeData(e, index, type, metaList, originMetaList, setMetaList);
    },
    [metaList, originMetaList, metaListChangeData],
  );

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    const url = 'api/setting/nm/option'; // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const body = {
      ...data,
      isPacsIncomingT1: data.isPacsIncomingT1,
      nmDicomTagId: metaList[0].dicomTagId,
      nmDicomTagValue: metaList[0].value,
      t1DicomTagId: metaList[1].dicomTagId,
      t1DicomTagValue: metaList[1].value,
    };

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData({ ...data });
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * dicom tag 가져오기 전체다이콤태그
   */
  const getDicomTag = async () => {
    const _result = await getDicomTagAll();
    setDicomTagList(_result.result);
  };

  /**
   * get nm option data
   */
  const getData = async () => {
    const url = 'api/setting/nm/option'; // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setMetaList([
            {
              ...r.result.nmDicomTag,
              isChanged: false,
              valid: true,
            },
            {
              ...r.result.t1DicomTag,
              isChanged: false,
              valid: true,
            },
          ]);

          setOriginMetaList([
            {
              ...r.result.nmDicomTag,
              isChanged: false,
              valid: true,
            },
            {
              ...r.result.t1DicomTag,
              isChanged: false,
              valid: true,
            },
          ]);

          setData({ ...r.result });
          setBackupData({ ...r.result });
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * metalist 유효성 모두 통과 여부 확인
   */
  useEffect(() => {
    if (!metaList) {
      setIsChanged(metaList !== originMetaList);
    } else {
      setIsChanged(!_.isEqual(metaList, originMetaList));
    }
  }, [metaList, originMetaList]);

  useEffect(() => {
    (async () => {
      await getDicomTag();
      await getData();
    })();
  }, []);

  console.log(metaList, 'metaList');

  return (
    <>
      <label>Option</label>
      <StyledReportWrap>
        <SettingWrapper>
          <strong>Default Threshold setting</strong>
          <SettingBox>
            <FlexBox>
              <ThresholdDisplay>{data.threshold}</ThresholdDisplay>

              <SliderWrapper>
                <Slider
                  min={1}
                  max={10}
                  value={data.threshold}
                  setValue={(value) =>
                    setData((prevState) => {
                      const copy = { ...prevState };
                      copy.threshold = value;
                      return copy;
                    })
                  }
                />
              </SliderWrapper>
            </FlexBox>
          </SettingBox>
        </SettingWrapper>
        <SettingWrapper>
          <strong>Protocol tag setting*</strong>
          <SettingBox>
            {metaList.map((item, i) => {
              return (
                <ElementBox key={i} className={item && item.tag && item.value && 'active'}>
                  <ElementTitle>{tagTitle[i]}</ElementTitle>

                  <ElementRow>
                    <ElementMetatag className={'title'}>Metatag</ElementMetatag>
                    <ElementValue>
                      <SelectSearchDicomTag
                        dicomTagList={dicomTagList}
                        value={item}
                        dataIdx={i}
                        onChangeFunc={metaListChange}
                      />
                    </ElementValue>
                    <ElementMetatag className={'title'}>Value</ElementMetatag>
                    <ElementValue>
                      <Input
                        className="input-value"
                        type="text"
                        placeholder="Value"
                        value={item?.value || ''}
                        onChange={(ev) => metaListChange(ev, i, 'VALUE')}
                      />
                    </ElementValue>
                  </ElementRow>
                </ElementBox>
              );
            })}
          </SettingBox>
        </SettingWrapper>
        <SettingWrapper>
          <strong>PACS incoming image setting*</strong>
          <SettingBox>
            <FlexBox>
              <FlexBox>
                <RadioLabel>
                  <Input
                    type="radio"
                    name="type"
                    checked={data.isPacsIncomingT1 === false}
                    onChange={() => setDataValue('isPacsIncomingT1', false)}
                  />
                  <span style={{ fontWeight: '700' }}>Sandwich NM</span>
                </RadioLabel>
              </FlexBox>
              <FlexBox>
                <RadioLabel>
                  <Input
                    type="radio"
                    name="type"
                    checked={data.isPacsIncomingT1 === true}
                    onChange={() => setDataValue('isPacsIncomingT1', true)}
                  />
                  <span style={{ fontWeight: '700' }} id="t1">
                    Sandwich NM + T1
                  </span>
                </RadioLabel>
              </FlexBox>
            </FlexBox>
            <SettingBoxList>
              <SettingBoxListTitle>Time out</SettingBoxListTitle>
              <SettingBoxListValue>
                <FlexBox>
                  <FlexBox>
                    <span className="l-title">minimum slice satisfied</span>
                    <InputBox>
                      <Input
                        type="text"
                        value={data.timeout.toString()}
                        onChange={(ev) => changeOnlyNumber(ev, 'timeout')}
                        onBlur={(ev) => blurHandler(ev, 'timeout')}
                      />
                    </InputBox>
                    <span className="suffix">s</span>
                  </FlexBox>
                  <FlexBox>
                    <span className="l-title">minimum slice not satisfied</span>
                    <InputBox>
                      <Input
                        type="text"
                        value={data.maximumTimeout.toString()}
                        onChange={(ev) => changeOnlyNumber(ev, 'maximumTimeout')}
                        onBlur={(ev) => blurHandler(ev, 'maximumTimeout')}
                      />
                    </InputBox>
                    <span className="suffix">s</span>
                  </FlexBox>
                  <Description>※ The timeout can be entered between 5 and 3600 seconds.</Description>
                </FlexBox>
              </SettingBoxListValue>
            </SettingBoxList>
            <SettingBoxList>
              <SettingBoxListTitle>Sandwich NM Image count</SettingBoxListTitle>
              <SettingBoxListValue>
                <FlexBox>
                  <FlexBox>
                    <span className="title">Slice Min</span>
                    <InputBox>
                      <Input
                        type="text"
                        value={data.nmSliceMin.toString()}
                        onChange={(ev) => changeOnlyNumber(ev, 'nmSliceMin')}
                        onBlur={(ev) => blurHandler(ev, 'nmSliceMin')}
                      />
                    </InputBox>
                  </FlexBox>
                  <FlexBox>
                    <span className="title">Slice Max</span>
                    <InputBox>
                      <Input
                        type="text"
                        value={data.nmSliceMax.toString()}
                        onChange={(ev) => changeOnlyNumber(ev, 'nmSliceMax')}
                        onBlur={(ev) => blurHandler(ev, 'nmSliceMax')}
                      />
                    </InputBox>
                  </FlexBox>
                  <Description>※ MIN/MAX can be entered between 0 and 10000.</Description>
                </FlexBox>
              </SettingBoxListValue>
            </SettingBoxList>
            {data.isPacsIncomingT1 && (
              <SettingBoxList>
                <SettingBoxListTitle>T1 Image count</SettingBoxListTitle>
                <SettingBoxListValue>
                  <FlexBox>
                    <FlexBox>
                      <span className="title">Slice Min</span>
                      <InputBox>
                        <Input
                          type="text"
                          value={data.t1SliceMin.toString()}
                          onChange={(ev) => changeOnlyNumber(ev, 't1SliceMin')}
                          onBlur={(ev) => blurHandler(ev, 't1SliceMin')}
                        />
                      </InputBox>
                    </FlexBox>
                    <FlexBox>
                      <span className="title">Slice Max</span>
                      <InputBox>
                        <Input
                          type="text"
                          value={data.t1SliceMax.toString()}
                          onChange={(ev) => changeOnlyNumber(ev, 't1SliceMax')}
                          onBlur={(ev) => blurHandler(ev, 't1SliceMax')}
                        />
                      </InputBox>
                    </FlexBox>
                    <Description>※ MIN/MAX can be entered between 0 and 10000.</Description>
                  </FlexBox>
                </SettingBoxListValue>
              </SettingBoxList>
            )}
          </SettingBox>
        </SettingWrapper>
      </StyledReportWrap>

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={isEditCondition}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default NMOption;
